import {Overlay} from "@athena-portal/components";
import {Document, ICustomerPortalService, Policy, Types, PostRequestedDoc} from "@falcon/services";
import * as React from "react";
import {Modal, ModalBody, ModalFooter, ModalHeader} from "reactstrap";
import {Checkbox} from "../../../../../components/inputs/Checkbox";
import {lazyInject} from "@athena-portal/core";
import {StorageSuccessModal} from "../../../../../components/modals/StorageSuccessModal";

interface IReceiveDocumentsByPostCancellationConfirmationModalProps {
    isOpen: boolean;
    onCancelReceiveByPost: () => void;
    onConfirmToReceiveDocumentsByPost: () => void;
    renderDocuments: React.ReactNode[];
    isAbleToPost: boolean;
    hasNoSelection: boolean;
}

const ReceiveDocumentsByPostCancellationConfirmationModal: React.FC<IReceiveDocumentsByPostCancellationConfirmationModalProps> = ({
        isOpen,
        onCancelReceiveByPost,
        onConfirmToReceiveDocumentsByPost,
        renderDocuments,
        isAbleToPost,
        hasNoSelection
    }) => (
    <Modal className="info-modal" isOpen={isOpen} toggle={onCancelReceiveByPost}>
        <ModalHeader toggle={onCancelReceiveByPost}>
            <div className="d-flex receive-by-post-title">
                <img src="/img/recycle-icon.png" alt="Info icon"/>
                <span className="ml-3 text-blue-2 fs-20 font-weight-medium">
                    Are you sure you want these documents to be posted?
                </span>
            </div>
        </ModalHeader>
        <ModalBody className="pt-0">
            <div className="font-weight-extra-light">
                <p>We care about our environmental impact and we've taken strides to reduce our carbon footprint.</p>
            </div>
            <div className="font-weight-semi-bold mb-0">
                <p>Did you know that printed documents are not required for taxing your car or showing to the
                    police?</p>
                <p>If you'd still like to receive your latest documents by post please select them below.</p>
            </div>
            {renderDocuments}
        </ModalBody>
        <ModalFooter>
            <div className="receive-by-post-btns">
                <button type="button" className="btn btn-primary btn-next btn-wide"
                        disabled={!isAbleToPost && !hasNoSelection}
                        onClick={onConfirmToReceiveDocumentsByPost}>{hasNoSelection ? "Save changes" : "Post selected documents"}
                </button>
                <button type="button" className="btn btn-white w-100 mb-0"
                        onClick={onCancelReceiveByPost}>Cancel changes
                </button>
            </div>
        </ModalFooter>
    </Modal>
);

interface IReceiveDocumentsByPostCheckboxSectionState {
    loading: boolean;
    showModal: boolean;
    sendDocumentsByPost: Map<string, boolean>;
    hasNoSelection: boolean;
    receivedDocumentsByPost: PostRequestedDoc[];
    showSuccessPopup: boolean;
    successPopupTitle: string;
    modalContent: React.ReactNode;
}

interface IProps {
    allDocuments?: Document[];
    policy: Policy;
}

export class PostRequestedDocumentsButton extends React.Component<IProps, IReceiveDocumentsByPostCheckboxSectionState> {

    @lazyInject(Types.CustomerPortalService)
    private customerPortalService: ICustomerPortalService;

    constructor(props: IProps) {
        super(props);

        this.state = {
            loading: true,
            showModal: false,
            hasNoSelection: false,
            sendDocumentsByPost: new Map<string, boolean>(),
            receivedDocumentsByPost: [],
            showSuccessPopup: false,
            successPopupTitle: "",
            modalContent: <></>
        };
    }

    public componentDidMount = () => {
        this.getAlreadyPostedDocuments();
    }

    public render = () => {
        return (
            <>
                <Overlay show={this.state.loading}/>
                {this.state.showSuccessPopup && (
                    <StorageSuccessModal
                        title={this.state.successPopupTitle}
                        explanationWording={this.state.modalContent}
                        anythingElseTitle={""}
                        explanationAnythingElseWording={""}
                        onClose={this.handleCloseSuccessPopup}/>
                )}
                <button type="button" className="btn btn-primary btn-blue-dashboard mx-auto mb-3 btn-center w-75"
                        id="requestDocumentsByPost"
                        name="requestDocumentsByPost" onClick={() => this.toggleDocumentPostModal()}>Request documents
                    by post
                </button>
                {!this.state.loading && <ReceiveDocumentsByPostCancellationConfirmationModal
                    isOpen={this.state.showModal}
                    onCancelReceiveByPost={this.cancelDocumentRequestByPost}
                    onConfirmToReceiveDocumentsByPost={this.postSelectedDocuments}
                    renderDocuments={this.renderAllCarDocuments()}
                    isAbleToPost={this.state.sendDocumentsByPost.size > 0}
                    hasNoSelection={this.state.hasNoSelection}
                />}
            </>
        );
    }

    private handleCloseSuccessPopup = (): void => {
        this.setState({showSuccessPopup: false})
    };

    private readonly getAlreadyPostedDocuments = () => {
        this.customerPortalService.getPostRequestedDocuments(this.props.policy.policyNumber).subscribe((receivedDocumentsByPost: PostRequestedDoc[]) => {
            this.setState({
                receivedDocumentsByPost,
                loading: false
            });
        });
    }

    private updateDocumentsToPost = () => {
        if (!this.state.hasNoSelection) {
            this.customerPortalService.setDocumentsRequestedByPost(Array.from(this.state.sendDocumentsByPost.keys()), this.props.policy.policyNumber).subscribe(() => {
                this.setState((prevState) => ({
                    receivedDocumentsByPost: [
                        ...prevState.receivedDocumentsByPost,
                        ...Array.from(prevState.sendDocumentsByPost.keys()).map((key) => ({
                            documentId: key,
                        })),
                    ],
                    sendDocumentsByPost: new Map<string, boolean>(),
                    loading: false,
                    showSuccessPopup: true,
                    successPopupTitle: "Document request was successful",
                    modalContent: <p>
                        You'll receive the documents you have requested in 5-7 working days
                    </p>
                }));
            });
        } else {
            this.setState({
                loading: false,
                showSuccessPopup: true,
                successPopupTitle: "Thanks for helping us do our part for the environment by going paper-free",
                modalContent: <p>
                    You can view or download your documents any time in your online account
                </p>
            });

        }

    }

    public renderAllCarDocuments = (): React.ReactNode[] => {
        const documentCheckBoxes: React.ReactNode[] = [];
        this.props.allDocuments?.forEach((document) => {
            documentCheckBoxes.push(this.createDocumentCheckBox(document));
        })
        return documentCheckBoxes;
    }

    private createDocumentCheckBox = (document: Document) => {
        return <Checkbox
            className="mt-3 ml-3"
            label={document.title}
            name={document.name}
            id={document.name}
            disabled={this.hasDocumentBeenPosted(document)}
            value={this.isCheckedDocument(document)}
            onChanged={(value) => this.onChangeDocumentPost(document, value)}/>
    }

    private isCheckedDocument = (document: Document) => {
        return this.hasDocumentBeenPosted(document) || this.state.sendDocumentsByPost.get(document.name) !== undefined;
    }

    private hasDocumentBeenPosted = (document: Document) => {
        return this.state.receivedDocumentsByPost.filter((receivedDocument) => receivedDocument.documentId === document.name && receivedDocument.policyVersionId === this.props.policy.interaction.agreementVersionId).length > 0;
    }

    private onChangeDocumentPost = (document: Document, newValue: boolean) => {
        this.setState(prevState => {
            const sendDocumentsByPost = new Map(prevState.sendDocumentsByPost);
            if (newValue) {
                sendDocumentsByPost.set(document.name, true);
            } else {
                sendDocumentsByPost.delete(document.name);
            }

            return {
                sendDocumentsByPost,
                hasNoSelection: sendDocumentsByPost.size === 0
            };
        });
    }

    private toggleDocumentPostModal = () => {
        this.setState({
            showModal: true,
        });
    }

    private postSelectedDocuments = () => {
        this.setState({
                showModal: false,
                loading: true,
            },
            () => this.updateDocumentsToPost());
    }

    private cancelDocumentRequestByPost = () => {
        this.setState({
            showModal: false,
            sendDocumentsByPost: new Map<string, boolean>(),
            showSuccessPopup: true,
            successPopupTitle: "Thanks for helping us do our part for the environment by going paper-free",
            modalContent: <p>
                No documents will be sent.
            </p>
        });
    }

}
